// React Query wiring for review & synthesis (step 5).
//
// Generation runs on the streaming Lambda Function URL (like assumptions): a
// clustering call over all dialog extractions can exceed API Gateway's 30s, so
// it streams from streamUrl. The proposal is persisted server-side at
// SYNTHESIS#LATEST; `useSynthesis` reads it, generation refreshes it, and
// promote/dismiss curate it. Promotion writes a native graph node (a
// job/challenge/opportunity tagged source:'synthesis'), so those mutations also
// invalidate the canvas + project queries.

import { useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useConfig } from '@/app/ConfigProvider';
import { useAuth } from '@/components/auth/AuthProvider';
import { consumeNdjsonStream } from '@/services/stream';
import { useStreamPreview } from '@/hooks/use-stream-preview';
import type { SynthesisCluster, SynthesisProposal, PromoteClusterInput, StreamedCluster } from '@/types/api';

export function useSynthesis(projectId: string) {
  return useQuery<SynthesisProposal | null>({
    queryKey: ['synthesis', projectId],
    queryFn: async () => {
      const res = await api.getSynthesis(projectId);
      if (!res.success) throw new Error(res.message || 'Failed to load synthesis');
      return res.data ?? null;
    },
  });
}

export function useGenerateSynthesis() {
  const queryClient = useQueryClient();
  const config = useConfig();
  const { getStreamAuthHeaders } = useAuth();
  const preview = useStreamPreview<StreamedCluster>('clusters');

  const mutation = useMutation<SynthesisCluster[], Error, string>({
    // Keyed so other surfaces (journey card, auto-run guard) can observe
    // in-flight runs via useSynthesisRunInFlight, whichever instance fired.
    mutationKey: ['generate-synthesis'],
    mutationFn: async (projectId: string) => {
      preview.reset();
      const data = await consumeNdjsonStream<{ clusters?: SynthesisCluster[] }>(
        `${config.api.streamUrl}/ai/synthesis`,
        { projectId },
        getStreamAuthHeaders(),
        preview.onChunk,
      );
      return data.clusters ?? [];
    },
    // Returning the invalidations keeps the mutation pending until the fresh
    // proposal is in cache, so the view swaps streamed previews for the real
    // rows without a flash of the stale list.
    onSuccess: (_data, projectId) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['synthesis', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['project', projectId] }),
      ]),
  });

  // Streamed clusters render as read-only previews while isPending — they have
  // no ids until the server persists the proposal at complete.
  return {
    ...mutation,
    streamedClusters: preview.items,
    streamStarted: preview.started,
    draftingTitle: preview.draftingTitle,
  };
}

// Whether ANY useGenerateSynthesis instance has a run in flight for this
// project — mutation state is per-instance, so the journey card can't see a
// run started in the view (or vice versa) without this.
export function useSynthesisRunInFlight(projectId: string) {
  const pending = useMutationState({
    filters: { mutationKey: ['generate-synthesis'], status: 'pending' },
    select: (m) => m.state.variables as string,
  });
  return pending.includes(projectId);
}

export function usePromoteCluster(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, PromoteClusterInput>({
    mutationFn: async (payload) => {
      const res = await api.promoteCluster(projectId, payload);
      if (!res.success) throw new Error(res.message || 'Failed to promote');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synthesis', projectId] });
      queryClient.invalidateQueries({ queryKey: ['canvas', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
}

export function useDismissCluster(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (clusterId) => {
      const res = await api.dismissCluster(projectId, clusterId);
      if (!res.success) throw new Error(res.message || 'Failed to dismiss');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synthesis', projectId] });
    },
  });
}

// Accepting attaches an item to an existing theme node (edge + recomputed
// counts), so it touches the graph — invalidate canvas + project too, like promote.
export function useAcceptAssignment(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (assignmentId) => {
      const res = await api.acceptAssignment(projectId, assignmentId);
      if (!res.success) throw new Error(res.message || 'Failed to accept assignment');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synthesis', projectId] });
      queryClient.invalidateQueries({ queryKey: ['canvas', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
}

export function useDismissAssignment(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (assignmentId) => {
      const res = await api.dismissAssignment(projectId, assignmentId);
      if (!res.success) throw new Error(res.message || 'Failed to dismiss assignment');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synthesis', projectId] });
    },
  });
}
